<script setup lang="ts">
import { computed, onMounted, nextTick, useTemplateRef } from 'vue';
import Dropdown from 'primevue/dropdown';
import type { FieldSchema } from '../../../types';
import { FILTER_MESSAGES, EMPTY_MESSAGES } from '@Constants';

const emits = defineEmits<{
  change: [value: any];
  onDropdownSelectValue: [];
}>();

const props = withDefaults(
  defineProps<{
    /** Teleport target for the overlay panel — pass `body` when the trigger sits inside a scroll or overflow container. */
    appendTo?: string;
    clearIcon?: string;
    clearIconStyle?: string;
    customClass?: string;
    customDropdownIconStyle?: string;
    customFilterInputStyle?: string;
    customInputStyle?: string;
    customTriggerStyle?: string;
    /** Option identity field. Without it PrimeVue deep-compares whole option objects on every render. */
    dataKey?: string;
    disabled?: boolean;
    dontShowClearTrigger?: boolean;
    dropdownIcon?: string;
    emptyMessage?: string;
    error?: string | boolean;
    field?: FieldSchema;
    hasFilter?: boolean;
    inputTextSize?: string;
    inputWidth?: string;
    isInlineEditing?: boolean;
    optionLabel?: string;
    optionValue?: string;
    options?: any[];
    placeholder?: string;
    showClear?: boolean;
  }>(),
  {
    appendTo: undefined,
    clearIconStyle: '',
    dataKey: undefined,
    dontShowClearTrigger: false,
    inputTextSize: 'text-sm',
    inputWidth: 'max-w-[290px] sm:max-w-[550px] xl:max-w-[620px]',
    isInlineEditing: false,
    placeholder: 'components.forms.placeholder.select',
  }
);

const model = defineModel();
const isOpen = defineModel<boolean>('isOpen', { default: false });

const inlineComponentRef = useTemplateRef<InstanceType<typeof Dropdown> | null>('inlineComponentRef');

const settings = computed(() => props?.field?.settings);
const disabled = computed(() => props?.field?.state.disabled || props?.disabled);
const sanitizedOptions = computed(() => props?.field?.edit?.options || props?.options);
const showClearTrigger = computed(
  () => !props.dontShowClearTrigger && !props?.field?.settings?.required && !!model.value
);
const filteredOptions = computed(() =>
  sanitizedOptions.value?.filter((opt: any) => {
    if (opt === null || opt === undefined) return false;

    if (typeof opt === 'object' && 'value' in opt && opt.value === null) {
      return false;
    }

    if (typeof opt === 'object' && opt.active === false) {
      return getOptionValue(opt) === model.value;
    }

    return true;
  })
);
const options = computed(() => {
  const list =
    props?.field?.name === 'primary_color_map'
      ? props?.field?.edit?.options?.map((color: any) => color.value)
      : filteredOptions.value;

  if (!Array.isArray(list) || list.length === 0 || (list.length === 1 && list[0] == null)) return [];

  return list;
});
// NOTE :: Inspect the final `options` list (post primary_color_map string mapping), not the raw
// sanitizedOptions — otherwise fields whose options get flattened to strings resolve a stale 'label'.
const hasChoiceObjectOptions = computed(() => {
  const list = options.value;
  if (!Array.isArray(list) || !list.length) return false;

  return list.every((option: any) => option && typeof option === 'object' && 'value' in option && 'label' in option);
});
const resolvedOptionLabel = computed(
  () => props?.field?.edit?.label || props?.optionLabel || (hasChoiceObjectOptions.value ? 'label' : undefined)
);
const resolvedOptionValue = computed(() => props?.field?.edit?.value || props?.optionValue);

onMounted(() => checkEditType());

// HELPERS

// NOTE :: Mirrors PrimeVue's own resolveFieldData so a dotted optionValue (e.g. `_id.$oid`) resolves.
function getOptionValue(option: any) {
  if (!resolvedOptionValue.value) return option;

  return resolvedOptionValue.value.split('.').reduce((resolved: any, key: string) => resolved?.[key], option);
}

// EVENTS

function checkEditType() {
  if (!props.isInlineEditing || !inlineComponentRef.value) return;

  nextTick(() => {
    if (!inlineComponentRef.value) return;
    inlineComponentRef.value.show();
  });
}

function onSelectValue(value: any) {
  if (props.isInlineEditing) {
    emits('onDropdownSelectValue');
    return;
  }

  emits('change', value);
}
</script>

<template>
  <div>
    <p
      v-if="
        !Array.isArray(field?.values) && (!options || options.length === 0) && field?.label && !props.isInlineEditing
      "
      class="py-1 text-xs italic text-gray-500"
    >
      {{ $t('components.emptyMessage.missingAnimalField', { field: $t(field?.label || '') }) }}
    </p>

    <p
      v-else-if="
        Array.isArray(field?.values) && (!options || options.length === 0) && field?.label && !props.isInlineEditing
      "
      class="py-1 text-xs italic text-gray-500"
    >
      {{ $t('components.emptyMessage.dropdownEmpty', { field: $t(field?.label || '') }) }}
    </p>

    <Dropdown
      v-bind="{ FILTER_MESSAGES, EMPTY_MESSAGES }"
      v-model="model"
      autoFilterFocus
      pt:emptymessage="text-sm"
      pt:item="text-sm"
      pt:item-label="overflow-hidden text-ellipsis max-w-[290px] sm:max-w-[550px] xl:max-w-[620px]"
      ref="inlineComponentRef"
      :appendTo
      :class="['relative flex h-9 w-full justify-between', customClass]"
      :clearIcon
      :dataKey
      :disabled
      :dropdownIcon
      :emptyFilterMessage="$t('components.emptyFilterMessage.dropdown')"
      :emptyMessage="$t(emptyMessage || 'components.emptyMessage.dropdown')"
      :emptySelectionMessage="$t('components.emptySelectionMessage.dropdown')"
      :filter="(hasFilter && options.length > 5) || options.length > 5"
      :filterMessage="$t('components.emptySelectionMessage.dropdown')"
      :filterResultMessage="$t('components.emptySelectionMessage.dropdown')"
      :invalid="!!error"
      :optionLabel="resolvedOptionLabel"
      :optionValue="resolvedOptionValue"
      :options
      :placeholder="$t(settings?.placeholder || placeholder)"
      :pt:clearIcon="clearIconStyle"
      :pt:dropdownIcon:class="customDropdownIconStyle"
      :pt:filterInput:class="['h-9 py-1 pl-3 text-sm', customFilterInputStyle]"
      :pt:input:class="[
        inputTextSize,
        customInputStyle,
        'flex items-center text-ellipsis overflow-hidden whitespace-nowrap',
        inputWidth,
        { 'af-field-error': !!error },
      ]"
      :pt:trigger:class="[customTriggerStyle, { 'text-red-600': !!error }]"
      :showClear="showClearTrigger"
      :virtualScrollerOptions="options.length > 5 ? { itemSize: 38 } : undefined"
      @change="onSelectValue"
      @hide="isOpen = false"
      @show="isOpen = true"
    />
  </div>
</template>
